import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuItemConfirm,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { highlightText } from "@/lib/highlight-text";
import { cn } from "@/lib/utils";
import { ConversationDTO } from "@shaarp/common";
import { MoreHorizontalIcon, PencilIcon, PinIcon, PinOffIcon } from "lucide-react";
import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useToastError } from "../ui/use-toast";
import { AvatarChatCompaniesEnriched } from "./avatar-chat-companies-enriched";
import { DialogRenameConversation } from "./dialog-rename-conversation";

export function ChatConversationLine({
  conversation,
  isSelected,
  searchTerm = "",
  onRemoved = () => {},
  onPinToggled = () => {},
  onRenamed = () => {},
}: {
  conversation: ConversationDTO;
  isSelected: boolean;
  searchTerm?: string;
  onRemoved?: () => void;
  onPinToggled?: (conversation: ConversationDTO) => void;
  onRenamed?: (conversation: ConversationDTO) => void;
}) {
  const api = useApi();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toastError = useToastError();

  const [isRenaming, setIsRenaming] = useState<boolean>(false);

  const isPinned = conversation.pinnedAt !== null;

  const title = conversation.title ?? t("chat.conversation.title");
  const matchedMessage = conversation.matchedMessage;

  // When the match comes from a message body, carry its id so the conversation
  // view can scroll straight to it (`?m=<messageId>`).
  const to = matchedMessage
    ? {
        pathname: `/chat/${conversation.id}`,
        search: `?m=${encodeURIComponent(matchedMessage.messageId)}`,
      }
    : `/chat/${conversation.id}`;

  const removeConversation = async (conversation: ConversationDTO) => {
    try {
      await api.delete(`/conversation/${conversation.id}`);
      if (isSelected) {
        navigate("/chat", { replace: true });
      }
      onRemoved();
    } catch (error) {
      toastError();
    }
  };

  const togglePin = async () => {
    try {
      const { data } = await api.patch<ConversationDTO>(
        `/conversation/${conversation.id}/pin`,
        { pinned: !isPinned }
      );
      onPinToggled(data);
    } catch (error) {
      toastError();
    }
  };

  return (
    <>
      {isRenaming && (
        <DialogRenameConversation
          conversation={conversation}
          onClose={() => setIsRenaming(false)}
          onRenamed={onRenamed}
        />
      )}
      <NavLink
        key={conversation.id}
        to={to}
        className={cn(
          "flex flex-col items-start gap-2 rounded-lg border border-border bg-background py-2 px-3 text-left text-sm transition-all duration-200 ease-out hover:bg-accent overflow-hidden ring-0 ring-inset ring-majorelle",
          isSelected && "border-majorelle ring-1"
        )}
      >
        <div className="flex flex-col w-full group">
          <div className="flex flex-row justify-between items-center w-full">
            <div className="overflow-hidden relative w-full font-semibold line-clamp-2">
              {highlightText(title, searchTerm)}
            </div>

            <div className="flex flex-row items-center px-3">
              <div className="flex flex-col justify-center">
                <AvatarChatCompaniesEnriched
                  conversation={conversation}
                  tooltipContentSide="top"
                  dropdownMenuContentAlign="center"
                />
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="w-7 h-7 shrink-0"
                  >
                    <MoreHorizontalIcon className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      togglePin();
                    }}
                  >
                    {isPinned ? (
                      <PinOffIcon className="w-4 h-4" />
                    ) : (
                      <PinIcon className="w-4 h-4" />
                    )}
                    {isPinned
                      ? t("chat.conversation.pin.unpin")
                      : t("chat.conversation.pin.pin")}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setIsRenaming(true);
                    }}
                  >
                    <PencilIcon className="w-4 h-4" />
                    {t("chat.conversation.rename.button")}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItemConfirm
                    className="text-red-500"
                    text={t("chat.conversation.delete.confirmation")}
                    isIrreversible={true}
                    onConfirm={() => removeConversation(conversation)}
                  >
                    {t("chat.conversation.delete.button")}
                  </DropdownMenuItemConfirm>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {matchedMessage && (
            <p className="mt-1 w-full text-xs text-muted-foreground line-clamp-2">
              {highlightText(`…${matchedMessage.snippet.trim()}…`, searchTerm)}
            </p>
          )}
        </div>
      </NavLink>
    </>
  );
}
